import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  LOCALE_ID,
  OnInit,
  Signal,
  signal,
  viewChild
} from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, filter, map, tap, throwError } from 'rxjs';
import { RecordsService } from '../records/records.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LeadTableRow, SearchLeadsResponse } from './leads.component.model';
import { leadRowDataToTableRow, leadToRowData } from './leads.component.mapper';
import {
  getColumnsWithWantedWidths,
  getTableColumnsFromTableRows,
  KeyValueLabel,
  LoaderComponent,
  LoadingButtonComponent,
  NoValue,
  SelectSignalComponent,
  StepEnum,
  TableColumn,
  TableData,
  TableV2Component,
  TextIconSignalInputComponent,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface
} from '@tribeauto/ngx-mobilite-material';
import { PageEvent } from '@angular/material/paginator';
import { AuthenticationService } from '../../authentication/authentication.service';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { UpperCasePipe } from '@angular/common';
import { ApplicationStore } from '../../../core/services/application-store.service';
import {
  getWebStoreIds,
  pointOfSalesToKeyValueLabelsWithAllOption,
  posAll
} from '../../../core/helpers/point-of-sale.helper';
import { UserService } from '../../user-connection/user.service';
import { AvailableSellerResponse, DEFAULT_FILTERS_LEADS, LeadsFilterData } from '../records/records.component.model';
import { initFiltersFormSignal } from '../../helpers/table.helper';
import { debounce, form, FormField, maxLength, minLength } from '@angular/forms/signals';
import { ReactiveFormsModule } from '@angular/forms';

const prefixRouteLeadsColumns = 'application.leads.table.';

@Component({
  selector: 'pvmo-leads',
  standalone: true,
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  imports: [
    LoaderComponent,
    UpperCasePipe,
    TableV2Component,
    LoadingButtonComponent,
    TranslatePipe,
    SelectSignalComponent,
    FormField,
    TextIconSignalInputComponent,
    ReactiveFormsModule
  ],
  templateUrl: './leads.component.html',
  styleUrl: './leads.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LeadsComponent implements OnInit, AfterViewInit {
  private readonly recordsService = inject(RecordsService);
  private readonly translateService = inject(TranslateService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LOCALE_ID);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  table: Signal<ElementRef> = viewChild.required('table');
  readonly router = inject(Router);
  readonly userService = inject(UserService);

  pos = input<string>();
  cckNumber = input<string>();
  clientName = input<string>();

  readonly filters = signal<LeadsFilterData>(DEFAULT_FILTERS_LEADS);
  readonly filterForm = form(this.filters, path => {
    minLength(path.cckNumber, 10);
    maxLength(path.cckNumber, 10);
    debounce(path.cckNumber, 'blur');
    maxLength(path.name, 50);
    debounce(path.name, 'blur');
  });

  isUserConnected = this.userService.isUserConnected;
  index = input<number>();

  availableSellers = signal<AvailableSellerResponse[] | null>(null);

  tveiListMap: KeyValueLabel[] = pointOfSalesToKeyValueLabelsWithAllOption(
    this.applicationStore.instant('userData')()?.pointOfSales,
    this.translateService
  );

  webstoreId = '';
  pageEvent = new PageEvent();

  leads = signal<LeadTableRow[] | undefined>(undefined);
  loadedLeads: LeadTableRow[] | null = null;
  columns: TableColumn[] = [];

  hasBeenInitializedIndex = false;

  constructor() {
    effect(() => {
      const sellerListMap = this.availableSellers();
      const leads = this.leads();
      if (sellerListMap && this.loadedLeads && leads) {
        for (const tableRow of this.loadedLeads) {
          const tableData = tableRow.tableData.find(
            data => data.key === this.translateService.instant(prefixRouteLeadsColumns + 'createdBy')
          );
          if (tableData) {
            const userLogin = tableData.value;
            const seller = sellerListMap.find(seller => seller.jossoLogin === userLogin);
            tableData.value = seller ? `${seller.firstName} ${seller.lastName}` : (userLogin ?? '');
            tableData.isLoading = false;
          }
        }
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.userService
      .getAvailableSellers({
        tveiNames: null,
        isExternal: this.applicationStore.instant('userData')()?.isExternal ? true : null
      })
      .subscribe(data => this.availableSellers.set(data));
    this.pageEvent.pageIndex = this.index() ?? 1;
    toObservable(this.filters, { injector: this.injector })
      .pipe(
        map(({ pos, cckNumber, name }) => ({ pos, cckNumber, name })),
        distinctUntilChanged((a, b) => a.pos?.key === b.pos?.key && a.cckNumber === b.cckNumber && a.name === b.name),
        debounceTime(100),
        filter(({ pos }) => this.filterForm().valid() && pos !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ pos, cckNumber, name }) => {
        this.cdr.markForCheck();
        if (pos?.value) {
          this.webstoreId = pos?.value;
          const isPageIndexed = !this.hasBeenInitializedIndex && !!this.index();
          this.hasBeenInitializedIndex = true;
          this.fetchLeads(isPageIndexed, name, cckNumber);
          this.updateLeadsFilters();
        }
      });
  }

  ngAfterViewInit(): void {
    initFiltersFormSignal(this.filters, this.pos(), this.cckNumber(), this.clientName(), this.tveiListMap);
  }

  onCellClick(tableData: TableData): void {
    this.authenticationService
      .generateInternalToken(tableData.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(accessToken => {
          this.updateLeadsFilters();
          const foundLead = this.leads()?.find(lead =>
            lead.tableData.some(data => data.key === tableData.key && data.value === tableData.value)
          );
          if (foundLead?.step === StepEnum.SUBSCRIPTION) {
            void this.router.navigate([`/subscription/${tableData.value}`], {
              queryParams: { token: accessToken, webStoreId: this.webstoreId }
            });
          } else {
            void this.router.navigate([`/simulation/${tableData.value}`], {
              queryParams: { token: accessToken }
            });
          }
        })
      )
      .subscribe();
  }

  onPageChanged(pageEvent: PageEvent): void {
    this.pageEvent.pageSize = pageEvent.pageSize;
    this.pageEvent.pageIndex = pageEvent.pageIndex;
    this.updateLeadsSignal(true);
  }

  searchWithCckNumber(): void {
    this.filterForm.cckNumber().markAsTouched();
    this.scrollToLeads();
  }

  searchWithClientName(): void {
    this.filterForm.name().markAsTouched();
    this.scrollToLeads();
  }

  hasAtLeastOneFilter(): boolean {
    return (
      this.filterForm.pos().value()?.key !== posAll.value ||
      !!this.filterForm.cckNumber().value() ||
      !!this.filterForm.name().value()
    );
  }

  resetFilters(): void {
    this.filters.set({
      pos: this.tveiListMap[0] ?? posAll,
      cckNumber: '',
      name: ''
    });
    this.pageEvent.pageIndex = 1;
    this.leads.set(undefined);
    this.loadedLeads = null;
  }

  onKeyDownResetFilters($event: KeyboardEvent): void {
    if ($event.key === 'Enter' || $event.code === 'Space') {
      this.resetFilters();
      $event.preventDefault();
    }
  }

  private updateLeadsSignal(pageIndex: boolean): void {
    this.leads.set(undefined);
    this.fetchLeads(pageIndex, this.filterForm.name().value(), this.filterForm.cckNumber().value());
  }

  private fetchLeads(pageIndex: boolean, name: string | NoValue, cckNumber: string | NoValue): void {
    this.leads.set(undefined);
    this.recordsService
      .searchLeads(
        {
          webStoreIds: getWebStoreIds(this.webstoreId, this.tveiListMap),
          names: name ? [name] : undefined,
          cckNumbers: cckNumber ? [cckNumber] : undefined
        },
        pageIndex ? this.pageEvent.pageIndex - 1 : 0,
        this.pageEvent.pageSize
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((searchLeadsResponse: SearchLeadsResponse) => {
          if (searchLeadsResponse) {
            this.pageEvent = {
              length: searchLeadsResponse.numberOfElements,
              pageSize: searchLeadsResponse.size,
              pageIndex: searchLeadsResponse.page + 1
            };

            return searchLeadsResponse.leadResponseList.map(lead =>
              leadRowDataToTableRow(leadToRowData(lead, this.translateService, this.locale), this.translateService)
            );
          }
          return [];
        }),
        tap(result => {
          this.loadedLeads = result;
          this.columns = getTableColumnsFromTableRows(this.loadedLeads);
          this.cdr.markForCheck();
        }),
        catchError(error => {
          return throwError(() => error);
        })
      )
      .subscribe(data => {
        this.leads.set(data);
      });
  }

  private scrollToLeads(): void {
    this.table().nativeElement.scrollIntoView({ behavior: 'smooth' });
    this.table().nativeElement.focus();
  }

  private updateLeadsFilters(): void {
    this.applicationStore.update('leadsFilters', {
      index: this.pageEvent.pageIndex,
      pos: this.filterForm.pos().value()?.value,
      cckNumber: this.filterForm.cckNumber().value(),
      clientName: this.filterForm.name().value()
    });
  }

  protected getColumns(): TableColumn[] {
    const widthMap: Record<string, number> = {
      [this.translateService.instant(`${prefixRouteLeadsColumns}.cckNumber`)]: 11,
      [this.translateService.instant(`${prefixRouteLeadsColumns}.date`)]: 12,
      [this.translateService.instant(`${prefixRouteLeadsColumns}.clientName`)]: 16,
      [this.translateService.instant(`${prefixRouteLeadsColumns}.insurancesNumber`)]: 10,
      [this.translateService.instant(`${prefixRouteLeadsColumns}.monthlyPayment`)]: 10,
      [this.translateService.instant(`${prefixRouteLeadsColumns}.seller`)]: 14
    };

    return getColumnsWithWantedWidths(this.columns, widthMap);
  }
}
